import { useEffect } from 'react'

/**
 * Studio-wide views, held in PortalApp rather than in `portalRoute` because
 * each is a view ACROSS every project rather than a step in the project/show
 * flow that route state models.
 *
 * 'none' is the front page — Needs Attention and the week. 'clients' is the
 * project list, which was the front page until 23 Sep 2026.
 * 'conversations' (25 Sep 2026) is the open email threads.
 * 'wrapUps' (26 Sep 2026) is the post-show reports. It began as a section on
 * Project Admin and Tom could not find it there — *"I don't want it under
 * the projects - it's not easy to find"* — so it moved up here beside Call
 * Sheets, which made the same journey for the same reason.
 * 'settings' (5 Nov 2026) is Team, the signed-in account and worker health
 * on one screen. The front page's Settings tile had been promising "Team ·
 * access · workers" while opening the Team page alone; this is the screen it
 * was describing.
 */
export type StudioView =
  | 'none'
  | 'clients'
  | 'calendar'
  | 'news'
  | 'callSheets'
  | 'wrapUps'
  | 'conversations'
  | 'settings'

const VIEWS: StudioView[] = [
  'none',
  'clients',
  'calendar',
  'news',
  'callSheets',
  'wrapUps',
  'conversations',
  'settings',
]

/**
 * Keeps the browser's back button working for a screen reached by React
 * state alone.
 *
 * PortalContext already syncs history for the project/show flow — built
 * 11 Sep 2026 after exactly this complaint, that one back-press exited
 * Portal OS to whatever page preceded it in the tab. Screens added since
 * (Calendar, News, Call Sheets, and one call sheet inside that list) were
 * never part of the snapshot it tracks, so opening one created no history
 * entry of ours and back kept walking straight out of the app. Same bug,
 * second time, because the fix lived somewhere the new screens did not pass
 * through — hence a hook, so the next one cannot miss it either.
 *
 * It writes onto the SAME history entry rather than keeping a second stack:
 * the push spreads whatever was already there, so PortalContext's own
 * popstate listener still restores its half untouched, and several of these
 * hooks can run side by side without fighting.
 *
 * ---
 *
 * **TWO WRITERS, ONE HISTORY ENTRY (27 Sep 2026).** The paragraph above was
 * only half true, and the missing half is a real bug.
 *
 * This hook spreads what it finds. **PortalContext does not.** Its own
 * history effect builds a fresh `NavSnapshot` — viewMode, clientRoute,
 * portalRoute, the active project/show/deliverable — and calls
 * `pushState(snapshot)` / `replaceState(snapshot)` with that object alone.
 * Every one of those writes therefore **deletes `studioView`** from the
 * entry, because the key simply is not in the object being written. It fires
 * on mount, again when `viewMode` settles to staff, and on every project or
 * route change after that.
 *
 * Two consequences, both of which this file now handles rather than
 * pretending they cannot happen:
 *
 * 1. **A history entry can exist that has no record of this key at all.**
 *    Landing on one by pressing back used to run `read(undefined)`, which
 *    returns the default — so back onto one of PortalContext's entries threw
 *    you out of Calendar or News and onto the week, for no reason the person
 *    pressing it could see. `onPop` now leaves the value alone unless the
 *    entry actually carries the key. An entry that never recorded a view has
 *    no opinion about the view; it should not get to impose one.
 *
 * 2. **Annotating is not navigating.** When the current entry has no record
 *    of this key, this hook now `replaceState`s onto it instead of pushing.
 *    Pushing there was inventing a back-step for something the person never
 *    did — arriving on a page, or PortalContext writing an entry underneath
 *    us — and it was the second half of why one tap could cost two
 *    back-presses.
 *
 * The proper fix is for PortalContext to spread the existing state the way
 * this file does, so the two writers stop clobbering each other. That file
 * is 2,600 lines and the change belongs in its own pass; until then, this
 * hook is written to survive the clobbering rather than to assume it away.
 */
export function useHistoryValue<T>(
  key: string,
  value: T,
  setValue: (next: T) => void,
  read: (raw: unknown) => T,
): void {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const state = (window.history.state ?? {}) as Record<string, unknown>

    if (key in state) {
      /* Our key is on this entry. Same value: nothing happened. Different
         value: a real navigation, which earns a real back-step. */
      if (read(state[key]) === value) return
      window.history.pushState({ ...state, [key]: value }, '', window.location.href)
      return
    }

    /* No record of this key on this entry — it was written by someone else
       (PortalContext) or by the browser on arrival. Label it where it
       stands. See the note above: this is annotation, not navigation. */
    window.history.replaceState({ ...state, [key]: value }, '', window.location.href)
  }, [key, value, read])

  useEffect(() => {
    if (typeof window === 'undefined') return
    function onPop(event: PopStateEvent) {
      const state = (event.state ?? {}) as Record<string, unknown>
      /* An entry that never recorded this key says nothing about it. Leaving
         the value alone is the honest reading; resetting it to the default
         is a guess, and it was a wrong one. */
      if (!(key in state)) return
      setValue(read(state[key]))
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [key, setValue, read])
}

function readView(raw: unknown): StudioView {
  return VIEWS.includes(raw as StudioView) ? (raw as StudioView) : 'none'
}

/** Back steps out of a studio view rather than out of Portal OS. */
export function useStudioViewHistory(view: StudioView, setView: (next: StudioView) => void): void {
  useHistoryValue('studioView', view, setView, readView)
}

function readId(raw: unknown): string | null {
  return typeof raw === 'string' && raw ? raw : null
}

/** Back steps out of one call sheet to the list, not out of Call Sheets. */
export function useOpenIdHistory(
  key: string,
  openId: string | null,
  setOpenId: (next: string | null) => void,
): void {
  useHistoryValue(key, openId, setOpenId, readId)
}
